import { useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormError('');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const fetchBody = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'website_alt') {
          fetchBody.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9ot9laftmtnfcfh0djg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: fetchBody.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // responseText is not JSON
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{
        name: '关于我们 - 韩漫画',
        description: '了解韩漫画品牌故事与发展历程，韩国漫画免费阅读平台，支持下拉式观看，收录众多好看的韩国漫画大全。联系我们获取更多信息。',
        url: 'https://www.hanmanhua.com/about',
        inLanguage: 'zh-CN',
      }} />

      <Navbar />

      <main className="pt-16">
        <div className="w-full bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-900 text-center mb-6">
              关于韩漫画
            </h1>
            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                <strong>韩漫画</strong>（hanmanhua.com）成立于2024年，是国内领先的韩国漫画免费阅读平台。我们致力于为中文读者提供高质量、正版授权的韩国漫画内容，支持下拉式观看，更新及时体验流畅。
              </p>
              <p>
                作为专业的<strong>韩国漫画网站</strong>，韩漫画收录了众多<strong>好看的韩国漫画大全</strong>，涵盖浪漫爱情、奇幻冒险、动作热血、悬疑推理、BL、GL、都市生活、古风等多种题材。我们的团队每天都会更新最新连载内容，确保读者能够第一时间追更喜爱的漫画作品。
              </p>
              <p>
                韩漫画采用先进的下拉式阅读技术，让漫画内容以垂直长条形式呈现，用户只需向下滑动即可连续阅读，无需翻页。这种阅读方式完美适配手机、平板和电脑等各类设备，为读者带来流畅自然的<strong>看漫画下拉式</strong>体验。
              </p>

              <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">我们的使命</h2>
              <p>
                韩漫画的使命是让每一位漫画爱好者都能轻松享受到高质量的韩国漫画内容。我们相信，好的漫画作品应该被更多人看到。因此，我们坚持<strong>韩漫画免费阅读</strong>的理念，为读者提供零门槛的阅读体验。
              </p>

              <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">核心优势</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {[
                  { icon: 'ri-book-read-line', title: '海量漫画资源', desc: '收录数千部热门韩国漫画，题材丰富多样，每周持续更新新作品。' },
                  { icon: 'ri-device-line', title: '下拉式阅读体验', desc: '采用先进的下拉式漫画阅读技术，无需翻页，滚动即可畅读。' },
                  { icon: 'ri-rocket-line', title: '极速更新', desc: '紧跟原作连载进度，第一时间同步更新，让您永远走在追更最前线。' },
                  { icon: 'ri-wallet-3-line', title: '完全免费阅读', desc: '坚持免费阅读理念，无需付费会员，所有漫画均可免费畅享。' },
                  { icon: 'ri-smartphone-line', title: '全设备适配', desc: '完美支持手机、平板和电脑，随时随地享受阅读乐趣。' },
                  { icon: 'ri-shield-user-line', title: '用户至上', desc: '以读者体验为核心，不断优化网站功能，提供最舒适的阅读环境。' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                      <i className={`${item.icon} text-lg text-accent-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900">{item.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">我们的信誉</h2>
              <p>
                自成立以来，韩漫画始终坚持以用户为中心的服务理念，赢得了广大漫画爱好者的信赖与好评。截至目前，平台月活跃用户已突破500万，日均阅读量超过200万次。我们与多家韩国知名漫画平台建立了合作关系，确保内容的丰富性和更新的及时性。
              </p>
              <p>
                我们严格遵守版权法规，所有漫画内容均经过正版授权，为读者提供合法、安全的阅读环境。同时，我们重视用户隐私保护，不会收集任何不必要的个人信息，为读者打造一个安心舒适的阅读空间。
              </p>

              <h2 className="text-xl font-bold font-heading text-foreground-900 mt-8 mb-4">联系我们</h2>
              <p>
                如果您有任何问题、建议或合作意向，欢迎通过以下表单与我们取得联系。我们的客服团队会在收到消息后的24小时内回复您。
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full py-10 md:py-14 bg-background-100">
          <div className="max-w-2xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 text-center mb-2">
              给我们留言
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              期待听到您的声音
            </p>

            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="space-y-5 bg-background-50 p-6 md:p-8 rounded-lg"
            >
              {formStatus === 'success' && (
                <div className="p-4 rounded-lg bg-accent-100 text-accent-800 text-sm">
                  感谢您的留言！我们会在24小时内回复您。
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-4 rounded-lg bg-red-50 text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  placeholder="请输入留言主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {/* Honeypot - hidden from users */}
              <div className="hp-field">
                <label htmlFor="website_alt">Website</label>
                <input
                  type="text"
                  id="website_alt"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 bg-primary-500 text-background-50 font-medium rounded-lg text-sm whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                {formStatus === 'loading' ? '提交中...' : '提交留言'}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}